import React, { useState } from "react";
import InquiryToCustomerInformationBox from "./InquiryToCustomerInformationBox";
import InquiryToCustomerDocumentsBox from "./InquiryToCustomerDocumentsBox";
import InquiryToCustomer, { InquiryToCustomerStatus } from "@/src/types/Entities/InquiryToCustomer";
import { Box, Button, Stack, Typography } from "@mui/material";
import { FormProvider, useForm } from "@/src/provider/Form/FormProvider";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import _isEmpty from "lodash/isEmpty";
import { Link, useNavigate } from "react-router";
import { getCaseLink } from "@/src/services/productService";
import * as messagesActions from "@/src/store/messages/actions";
import { allowMissingDocument } from "@/src/services/inquiryToCustomerService";
import HighlightBox from "@/src/components/Box/HighlightBox/HighlightBox";
import { getNewMessageLink } from "@/src/services/messagingService";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/src/services/reactQuery/reactQueryService";

type InquiryToCustomerInformationAndDocumentsProps = {
  inquiryToCustomer: InquiryToCustomer;
  product: AbstractCase;
  updateInquiryToCustomer: (data: Partial<InquiryToCustomer>) => Promise<InquiryToCustomer>;
};

export default function InquiryToCustomerInformationAndDocuments({
  inquiryToCustomer,
  product,
  updateInquiryToCustomer,
}: InquiryToCustomerInformationAndDocumentsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  if (!inquiryToCustomer.requestedDocuments) {
    return null;
  }

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);
    const payload: Partial<InquiryToCustomer> = {
      status: InquiryToCustomerStatus.STATUS_DONE,
      doneBy: "customer",
    };
    switch (inquiryToCustomer.inquiryType) {
      case "documents":
        payload.inquiryTextDocumentsCustomer = values.inquiryTextDocumentsCustomer;
        break;
      case "information":
        payload.inquiryTextInformationCustomer = values.inquiryTextInformationCustomer;
        break;
      case "informationAndDocuments":
        payload.inquiryTextDocumentsCustomer = values.inquiryTextDocumentsCustomer;
        payload.inquiryTextInformationCustomer = values.inquiryTextInformationCustomer;
        break;
      default:
        break;
    }

    await updateInquiryToCustomer(payload);
    await queryClient.invalidateQueries({ queryKey: queryKeys.collections("/inquiry_to_customers") });
    setIsLoading(false);

    messagesActions.addMessage({
      type: "success",
      text: "Rückmeldung erfolgreich abgegeben",
    });
    navigate(getCaseLink(product) + "/anfrage/beantwortet");
  };

  let initialValues = {
    inquiryTextInformationCustomer: inquiryToCustomer.inquiryTextInformationCustomer,
    inquiryTextDocumentsCustomer: "",
  };

  inquiryToCustomer.requestedDocuments.forEach((requestedDocument, index) => {
    // @ts-ignore
    initialValues["requestedDocumentMissing" + index] = false;
  });

  return (
    <ContentBox>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <InquiryToCustomerInformationAndDocumentsForm
          inquiryToCustomer={inquiryToCustomer}
          product={product}
          isLoading={isLoading}
          updateInquiryToCustomer={updateInquiryToCustomer}
        />
      </FormProvider>
    </ContentBox>
  );
}

type InquiryToCustomerInformationAndDocumentsFormProps = {
  inquiryToCustomer: InquiryToCustomer;
  product: AbstractCase;
  isLoading: boolean;
  updateInquiryToCustomer: (data: Partial<InquiryToCustomer>) => Promise<InquiryToCustomer>;
};

const InquiryToCustomerInformationAndDocumentsForm = ({
  inquiryToCustomer,
  product,
  isLoading,
  updateInquiryToCustomer,
}: InquiryToCustomerInformationAndDocumentsFormProps) => {
  const { handleSubmit, values, errors } = useForm();

  const isDocumentsRequest = ["documents", "informationAndDocuments"].includes(inquiryToCustomer.inquiryType);
  const isInformationRequest = ["information", "informationAndDocuments"].includes(inquiryToCustomer.inquiryType);

  const documentInfoMissing =
    isDocumentsRequest &&
    inquiryToCustomer.requestedDocuments?.some((requestedDocument, index) => {
      return (
        requestedDocument.temporaryFilesToCombine.length < 1 &&
        (!allowMissingDocument(requestedDocument, inquiryToCustomer) || values["requestedDocumentMissing" + index] === false)
      );
    });

  return (
    <form onSubmit={handleSubmit}>
      {isInformationRequest && <InquiryToCustomerInformationBox inquiryToCustomer={inquiryToCustomer} />}
      {isDocumentsRequest && (
        <Box sx={{ marginTop: isInformationRequest ? "1.5rem" : 0 }}>
          <InquiryToCustomerDocumentsBox
            inquiryToCustomer={inquiryToCustomer}
            product={product}
            hideMediaObjects={isInformationRequest}
            updateInquiryToCustomer={updateInquiryToCustomer}
          />
        </Box>
      )}
      <Box sx={{ marginTop: "2rem" }}>
        <ButtonLoading
          fullWidth
          variant={"contained"}
          color={"primary"}
          type={"submit"}
          isLoading={isLoading}
          disabled={documentInfoMissing || !_isEmpty(errors)}
        >
          Rückmeldung abschicken
        </ButtonLoading>
        {(!_isEmpty(errors) || documentInfoMissing) && (
          <Typography variant={"caption"} component={"div"} sx={{ color: "red", textAlign: "center", marginTop: "1rem" }}>
            Es liegen leider nicht alle angeforderten Informationen/Dokumente vor. Bitte prüfen Sie Ihre Angaben oben.
          </Typography>
        )}
      </Box>
      <HighlightBox sx={{ marginTop: "2rem" }}>
        <Stack
          spacing={2}
          sx={{
            justifyContent: "center",
          }}
        >
          <Typography sx={{ textAlign: "center" }}>
            Sie können uns jetzt noch keine Rückmeldung geben, weil Sie gerade auf etwas warten oder klären müssen? Dann lassen Sie diese Anfrage bitte einfach
            auf und melden sich erst hier bei uns zurück, wenn Sie alles klären konnten.
          </Typography>
          <Typography sx={{ textAlign: "center" }}>
            Sie haben Fragen oder möchten uns abseits dieser Rückmeldung etwas anderes mitteilen? Dann schreiben Sie uns gerne eine Nachricht.
          </Typography>
          <Button component={Link} to={getNewMessageLink(product.name, product.id)}>
            Nachricht schreiben
          </Button>
        </Stack>
      </HighlightBox>
    </form>
  );
};
